import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-confirm-typed.css?inline';
import '../pk-modal/pk-modal.js';
import '../pk-button/pk-button.js';

/**
 * `<pk-confirm-typed>` — typed-name confirmation gate for destructive admin actions.
 *
 * Adjacent to `<pk-confirm-modal>`. This variant is the admin-portal write-side
 * dialect: it always renders the typed gate, supports a markdown-rendered
 * consequences body, and emits `pk-confirmed` / `pk-cancelled` (the orchestrator
 * admin spec event names) rather than the legacy `confirmed` / `cancelled`.
 *
 * Composes on top of `<pk-modal>`. The action button stays disabled until the
 * user types `target-name` verbatim into the gate input. Used for "type the
 * tenant ID / user email to delete" gates.
 *
 * Attributes:
 *   - `open`              — boolean; shows/hides the modal.
 *   - `target-name`       — string the user must type verbatim to enable the
 *                            action button. Required for the gate to engage; when
 *                            absent, the button is always enabled.
 *   - `verb`              — action button text. Default `Delete`.
 *   - `consequences-md`   — markdown body rendered below the heading. We render
 *                            a conservative subset (paragraphs, line breaks,
 *                            `**bold**`, `` `code` ``) inline; no remote markdown
 *                            dep is pulled in.
 *   - `title`             — dialog heading. Default `Are you sure?`.
 *   - `cancel-label`      — cancel button text. Default `Cancel`.
 *   - `variant`           — `primary` | `danger`. Default `danger`.
 *
 * Events:
 *   - `pk-confirmed` — fired when the action button is clicked while enabled.
 *                      `detail: { targetName }`.
 *   - `pk-cancelled` — fired on cancel-button click, backdrop, Escape, or close X.
 */
export class PkConfirmTyped extends HTMLElement {
    static readonly tagName = 'pk-confirm-typed';

    static get observedAttributes(): string[] {
        return [
            'open', 'target-name', 'verb', 'consequences-md',
            'title', 'cancel-label', 'variant',
        ];
    }

    private readonly _root: ShadowRoot;
    private _typed = '';

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
        this.addEventListener('pk-cancel', this._onModalClose);
    }

    disconnectedCallback(): void {
        this.removeEventListener('pk-cancel', this._onModalClose);
    }

    attributeChangedCallback(name: string): void {
        if (name === 'open' && !this.hasAttribute('open')) {
            this._typed = '';
        }
        this.render();
    }

    /** Programmatically open the modal. */
    show(): void {
        this.setAttribute('open', '');
    }

    /** Programmatically close the modal. */
    hide(): void {
        this.removeAttribute('open');
    }

    private readonly _onModalClose = (e: Event): void => {
        // pk-modal v0.4 fires pk-cancel (and pk-modal-close for back-compat).
        // We listen to pk-cancel only — listening to both would double-fire.
        e.stopPropagation();
        this._emitCancelled();
    };

    private readonly _onInput = (e: Event): void => {
        const input = e.target as HTMLInputElement;
        this._typed = input.value;
        this.render();
    };

    private readonly _onConfirmClick = (): void => {
        if (!this._isUnlocked()) return;
        this.dispatchEvent(new CustomEvent('pk-confirmed', {
            detail: { targetName: this.getAttribute('target-name') ?? '' },
            bubbles: true,
            composed: true,
        }));
    };

    private readonly _onCancelClick = (): void => {
        this._emitCancelled();
    };

    private _emitCancelled(): void {
        this.removeAttribute('open');
        this.dispatchEvent(new CustomEvent('pk-cancelled', {
            bubbles: true,
            composed: true,
        }));
    }

    private _isUnlocked(): boolean {
        const target = this.getAttribute('target-name') ?? '';
        if (!target) return true;
        return this._typed === target;
    }

    /**
     * Conservative inline markdown rendering for `consequences-md`. We
     * deliberately avoid pulling in a markdown library: this is admin-confirm
     * body copy, not arbitrary user content, and the substitution surface is
     * the same shape we'd hand-roll for "warning paragraph with the odd bold
     * or code span". Supported:
     *
     *   - blank-line-separated paragraphs
     *   - inline `**bold**`
     *   - inline `` `code` ``
     *
     * Anything else passes through as literal text — there's no raw-HTML
     * escape hatch, so the result is safe to template into the DOM.
     */
    private _renderConsequences(md: string): TemplateResult[] {
        const paragraphs = md.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
        return paragraphs.map(p => this._renderInline(p));
    }

    private _renderInline(text: string): TemplateResult {
        // Tokenise into runs of plain text, **bold**, and `code`. Build a
        // TemplateResult array so lit-html sanitises each text node.
        const parts: Array<{ kind: 'text' | 'bold' | 'code'; value: string }> = [];
        let i = 0;
        while (i < text.length) {
            if (text[i] === '*' && text[i + 1] === '*') {
                const close = text.indexOf('**', i + 2);
                if (close !== -1) {
                    parts.push({ kind: 'bold', value: text.slice(i + 2, close) });
                    i = close + 2;
                    continue;
                }
            }
            if (text[i] === '`') {
                const close = text.indexOf('`', i + 1);
                if (close !== -1) {
                    parts.push({ kind: 'code', value: text.slice(i + 1, close) });
                    i = close + 1;
                    continue;
                }
            }
            // Accumulate plain run up to the next special char.
            let j = i + 1;
            while (j < text.length && text[j] !== '*' && text[j] !== '`') j++;
            parts.push({ kind: 'text', value: text.slice(i, j) });
            i = j;
        }
        return html`<p class="consequence">${parts.map(p =>
            p.kind === 'bold' ? html`<strong>${p.value}</strong>` :
            p.kind === 'code' ? html`<code>${p.value}</code>` :
            html`${p.value}`,
        )}</p>`;
    }

    private template(): TemplateResult {
        const isOpen     = this.hasAttribute('open');
        const target     = this.getAttribute('target-name')     ?? '';
        const verb       = this.getAttribute('verb')            ?? 'Delete';
        const md         = this.getAttribute('consequences-md') ?? '';
        const title      = this.getAttribute('title')           ?? 'Are you sure?';
        const cancel     = this.getAttribute('cancel-label')    ?? 'Cancel';
        const variantRaw = this.getAttribute('variant')         ?? 'danger';
        const variant    = variantRaw === 'primary' ? 'primary' : 'danger';
        // Spec § 3.4: wider modal when there is substantial body content.
        const size       = md ? 'medium' : 'small';
        const unlocked   = this._isUnlocked();

        return html`
            <style>${styles}</style>
            <pk-modal
                ?open=${isOpen}
                title=${title}
                size=${size}
                dismissible="escape"
                variant=${variant === 'danger' ? 'danger' : nothing}
            >
                ${md ? html`<div class="consequences">${this._renderConsequences(md)}</div>` : ''}

                ${target ? html`
                    <label class="gate-label" for="confirm-input">
                        Type
                        <code class="gate-target">${target}</code>
                        to confirm
                    </label>
                    <input
                        id="confirm-input"
                        class="gate-input ${unlocked ? 'unlocked' : ''}"
                        type="text"
                        autocomplete="off"
                        spellcheck="false"
                        .value=${this._typed}
                        @input=${this._onInput}
                    />
                ` : ''}

                <pk-button
                    slot="secondary"
                    variant="ghost"
                    type="button"
                    @click=${this._onCancelClick}
                >${cancel}</pk-button>
                <pk-button
                    slot="primary"
                    variant=${variant}
                    type="button"
                    ?disabled=${!unlocked}
                    @click=${this._onConfirmClick}
                >${verb}</pk-button>
            </pk-modal>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkConfirmTyped.tagName)) {
    customElements.define(PkConfirmTyped.tagName, PkConfirmTyped);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-confirm-typed': PkConfirmTyped;
    }
}
